/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * Menu Item Form Modal Component
 * Modal dialog for creating and editing menu items
 */

import { CheckCircle, Loader2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Department } from '../../../types/department';
import {
  CreateMenuItemRequest,
  MenuCategory,
  MenuItem,
  MenuItemStatus,
} from '../../../types/menu';
import { DropdownSelect } from '../../common/DropdownSelect';
import { Input } from '../../common/Input';

interface MenuItemFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateMenuItemRequest) => void | Promise<void>;
  item?: MenuItem | null;
  categories: MenuCategory[];
  departments: Department[];
  isLoading?: boolean;
}

export const MenuItemFormModal: React.FC<MenuItemFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  item,
  categories,
  departments,
  isLoading = false,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [status, setStatus] = useState<MenuItemStatus>('active');
  const [sku, setSku] = useState('');
  const [isTaxable, setIsTaxable] = useState(true);
  const [taxRate, setTaxRate] = useState('');
  const [preparationTime, setPreparationTime] = useState('');
  const [calories, setCalories] = useState('');
  const [errors, setErrors] = useState<{ name?: string; basePrice?: string }>(
    {},
  );
  const [justCreated, setJustCreated] = useState(false);

  // Persists contextual defaults across successive item creations so the user
  // doesn't have to re-select category/department/status each time.
  const persistedRef = useRef({
    categoryId: '',
    departmentId: '',
    status: 'active' as MenuItemStatus,
    isTaxable: true,
    taxRate: '',
  });

  const isEditMode = !!item;

  // Reset form when modal opens/closes or item changes
  useEffect(() => {
    if (isOpen) {
      if (item) {
        setName(item.name);
        setDescription(item.description || '');
        setBasePrice(item.base_price.toString());
        setCategoryId(item.category_id || '');
        setDepartmentId(item.department_id || '');
        setStatus(item.status);
        setSku(item.sku || '');
        setIsTaxable(item.is_taxable);
        setTaxRate(item.tax_rate?.toString() || '');
        setPreparationTime(item.preparation_time?.toString() || '');
        setCalories(item.calories?.toString() || '');
      } else {
        // Clear item-specific fields; restore last-used contextual defaults.
        setName('');
        setDescription('');
        setBasePrice('');
        setSku('');
        setPreparationTime('');
        setCalories('');
        setCategoryId(persistedRef.current.categoryId);
        setDepartmentId(persistedRef.current.departmentId);
        setStatus(persistedRef.current.status);
        setIsTaxable(persistedRef.current.isTaxable);
        setTaxRate(persistedRef.current.taxRate);
        setJustCreated(false);
      }
      setErrors({});
    }
  }, [isOpen, item]);

  const validateForm = (): boolean => {
    const newErrors: { name?: string; basePrice?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Item name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!basePrice.trim()) {
      newErrors.basePrice = 'Price is required';
    } else if (
      Number.isNaN(parseFloat(basePrice)) ||
      parseFloat(basePrice) < 0
    ) {
      newErrors.basePrice = 'Please enter a valid price';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    await onSubmit({
      outlet_id: '', // Will be set by parent
      name: name.trim(),
      description: description.trim() || undefined,
      base_price: parseFloat(basePrice),
      category_id: categoryId || undefined,
      department_id: departmentId || undefined,
      status,
      sku: sku.trim() || undefined,
      is_taxable: isTaxable,
      tax_rate: taxRate ? parseFloat(taxRate) : undefined,
      preparation_time: preparationTime
        ? parseInt(preparationTime, 10)
        : undefined,
      calories: calories ? parseInt(calories, 10) : undefined,
    });

    if (!isEditMode) {
      // Persist contextual values so the next item starts from these defaults.
      persistedRef.current = {
        categoryId,
        departmentId,
        status,
        isTaxable,
        taxRate,
      };
      setJustCreated(true);
      setName('');
      setDescription('');
      setBasePrice('');
      setSku('');
      setPreparationTime('');
      setCalories('');
      setErrors({});
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEditMode ? 'Edit Menu Item' : 'Create Menu Item'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-4">
            {/* Basic Info */}
            <div className="col-span-2">
              <Input
                label="Item Name"
                value={name}
                onChange={(value) => setName(String(value))}
                placeholder="e.g., Grilled Chicken"
                error={errors.name}
                disabled={isLoading}
              />
            </div>

            <div className="col-span-2">
              <div className="input-container">
                <label htmlFor="item-description" className="input-label">
                  Description (Optional)
                </label>
                <textarea
                  id="item-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this item"
                  className="input-field h-20 resize-none"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <Input
                label="Base Price"
                type="number"
                value={basePrice}
                onChange={(value) => setBasePrice(String(value))}
                placeholder="0.00"
                error={errors.basePrice}
                disabled={isLoading}
              />
            </div>

            <div>
              <Input
                label="SKU (Optional)"
                value={sku}
                onChange={(value) => setSku(String(value))}
                placeholder="e.g., GRCH-001"
                disabled={isLoading}
              />
            </div>

            {/* Category & Department */}
            <DropdownSelect
              label="Category"
              value={categoryId}
              onChange={setCategoryId}
              options={[
                { value: '', label: 'No Category' },
                ...categories.map((cat) => ({
                  value: cat.id,
                  label: cat.name,
                })),
              ]}
              searchable={categories.length > 10}
              disabled={isLoading}
            />

            <DropdownSelect
              label="Department"
              value={departmentId}
              onChange={setDepartmentId}
              options={[
                { value: '', label: 'No Department' },
                ...departments.map((dept) => ({
                  value: dept.id,
                  label: dept.name,
                })),
              ]}
              searchable={departments.length > 10}
              disabled={isLoading}
            />

            {/* Status */}
            <DropdownSelect
              label="Status"
              value={status}
              onChange={(v) => setStatus(v as MenuItemStatus)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'out_of_stock', label: 'Out of Stock' },
              ]}
              disabled={isLoading}
            />

            {/* Preparation Time */}
            <div>
              <Input
                label="Prep Time (minutes)"
                type="number"
                value={preparationTime}
                onChange={(value) => setPreparationTime(String(value))}
                placeholder="e.g., 15"
                disabled={isLoading}
              />
            </div>

            {/* Tax Settings */}
            <div className="col-span-2 border-t border-gray-200 pt-4 mt-2">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Tax Settings
              </h4>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isTaxable}
                    onChange={(e) => setIsTaxable(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-pos-primary focus:ring-pos-primary"
                    disabled={isLoading}
                  />
                  <span className="text-sm text-gray-700">Taxable</span>
                </label>
                {isTaxable && (
                  <div className="flex-1 max-w-[150px]">
                    <Input
                      label="Tax Rate (%)"
                      type="number"
                      value={taxRate}
                      onChange={(value) => setTaxRate(String(value))}
                      placeholder="0.00"
                      disabled={isLoading}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Nutritional Info */}
            <div className="col-span-2 border-t border-gray-200 pt-4 mt-2">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Nutritional Info (Optional)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Calories"
                  type="number"
                  value={calories}
                  onChange={(value) => setCalories(String(value))}
                  placeholder="e.g., 450"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3 pt-6 mt-4 border-t border-gray-200">
            {justCreated && !isEditMode ? (
              <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                <CheckCircle size={15} />
                Item created — add another below
              </span>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isLoading}
              >
                {isEditMode ? 'Cancel' : 'Done'}
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors disabled:opacity-50"
              >
                {isLoading && <Loader2 size={18} className="animate-spin" />}
                <span>{isEditMode ? 'Update Item' : 'Create Item'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
